import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

import { localStorageLocaleKey } from '../content/shared';
import { Seo } from '../components/Seo';
import { getSeoConfig } from '../lib/seo';
import { getLocaleFromPath, legacyRedirects, localizedPaths } from '../lib/routes';
import { DataProtectionPage } from '../pages/DataProtectionPage';
import { LandingPage } from '../pages/LandingPage';
import { LegalNoticePage } from '../pages/LegalNoticePage';
import { NotFoundPage } from '../pages/NotFoundPage';

function getPreferredLocale(): 'en' | 'de' {
  const stored = window.localStorage.getItem(localStorageLocaleKey);

  if (stored === 'en' || stored === 'de') {
    return stored;
  }

  return navigator.language.toLowerCase().startsWith('de') ? 'de' : 'en';
}

function HomeRedirect() {
  const navigate = useNavigate();

  useEffect(() => {
    const locale = getPreferredLocale();
    void navigate(localizedPaths.landing[locale], { replace: true });
  }, [navigate]);

  return (
    <>
      <Seo config={getSeoConfig('landing', 'en')} />
      <main className="redirect-screen" aria-live="polite">
        <p>Redirecting to the preferred language...</p>
      </main>
    </>
  );
}

function LegacyRedirect({ to }: { to: string }) {
  return <Navigate to={to} replace />;
}

function NotFoundRoute() {
  const location = useLocation();

  return <NotFoundPage locale={getLocaleFromPath(location.pathname)} />;
}

function ScrollRestoration() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    window.scrollTo({ top: 0 });
  }, [location.pathname, location.hash]);

  return null;
}

export function AppRoutes() {
  return (
    <>
      <ScrollRestoration />
      <Routes>
        <Route path="/" element={<HomeRedirect />} />
        <Route path="/en" element={<LandingPage locale="en" />} />
        <Route path="/en/" element={<LandingPage locale="en" />} />
        <Route path="/de" element={<LandingPage locale="de" />} />
        <Route path="/de/" element={<LandingPage locale="de" />} />
        <Route path="/en/legal-notice" element={<LegalNoticePage locale="en" />} />
        <Route path="/en/legal-notice/" element={<LegalNoticePage locale="en" />} />
        <Route path="/de/impressum" element={<LegalNoticePage locale="de" />} />
        <Route path="/de/impressum/" element={<LegalNoticePage locale="de" />} />
        <Route path="/en/data-protection" element={<DataProtectionPage locale="en" />} />
        <Route path="/en/data-protection/" element={<DataProtectionPage locale="en" />} />
        <Route path="/de/datenschutz" element={<DataProtectionPage locale="de" />} />
        <Route path="/de/datenschutz/" element={<DataProtectionPage locale="de" />} />
        <Route path="/about" element={<LegacyRedirect to={legacyRedirects['/about']} />} />
        <Route path="/contact" element={<LegacyRedirect to={legacyRedirects['/contact']} />} />
        <Route path="/404" element={<NotFoundPage locale="en" />} />
        <Route path="/404/" element={<NotFoundPage locale="en" />} />
        <Route path="*" element={<NotFoundRoute />} />
      </Routes>
    </>
  );
}
